import { StickerSquare } from "untitledui-js-base";
import {
  ActivityItem,
  AddNoteMutation,
  DeleteNoteMutation,
  EditNoteMutation
} from "./types";
import { TimeLine, TimeLineItem } from "shared/components/TimeLine";
import { BulletItem } from "shared/app/ActivityLog/BulletItem";
import { ActivityNote } from "shared/app/ActivityLog/ActivityNote";
import { NoteForm } from "./components/NoteForm";
import { useAuthContext } from "providers/Auth";
import { useMemo } from "react";
import { instance } from "api/apiBase";
import { getMentionNotificationId, isUnread, useMarkRead } from "./helpers";
import { useQueryClient } from "@tanstack/react-query";

export const ActivitiesList = ({
  applicationId,
  addNoteMeta,
  editNoteMeta,
  deleteNoteMeta,
  activityLogs,
  propertyName
}: {
  addNoteMeta: AddNoteMutation;
  editNoteMeta: EditNoteMutation;
  deleteNoteMeta: DeleteNoteMutation;
  applicationId: string;
  activityLogs: ActivityItem[];
  propertyName?: string;
}) => {
  const { auth } = useAuthContext();
  const queryClient = useQueryClient();

  const ureadNotes = useMemo(() => {
    const notes: { id: string; read: boolean }[] = [];
    for (const activity of activityLogs)
      if (activity.activity_type === "note" && isUnread(activity, auth?.id!))
        notes.push({
          id: String(getMentionNotificationId(activity, auth?.id!) ?? ""),
          read: false
        });

    return notes;
  }, [activityLogs, auth?.id]);

  const getElById = (id: string) =>
    document.querySelector<HTMLElement>(`[data-activity-id="${id}"]`);

  async function markReadBatch(ids: string[]) {
    await instance
      .post("/portal/v1/notifications/mark_as_read", {
        ids: ids.map(Number)
      })
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"]
        });
      });
  }

  useMarkRead(ureadNotes, getElById, markReadBatch, {
    threshold: 0.5,
    dwellMs: 600,
    flushMs: 300
  });

  return (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b">
        <NoteForm
          onSubmit={async (data) => {
            await addNoteMeta.mutateAsync(data);
          }}
          applicationId={applicationId}
          propertyName={propertyName}
          isLoading={addNoteMeta.isPending}
        />
      </div>

      <div className="h-full">
        {!activityLogs.length ? (
          <div className="mt-8 flex flex-auto flex-col items-center justify-center">
            <div className="mb-6 flex h-[64px] w-[64px] items-center justify-center rounded-full bg-alpha-40 p-5">
              <StickerSquare size="24" className="stroke-content-500" />
            </div>
            <p className="text-sm font-medium text-content-300">No logs yet</p>
          </div>
        ) : (
          <div className="px-6 size-full flex-1 overflow-x-hidden overflow-y-auto hide-scroll bg-background-900">
            <TimeLine wrapperClassName="ml-4 border-alpha-100 pt-1 w-full">
              {activityLogs.map((activity) => {
                let decisionStatus = "";
                if (
                  activity.details &&
                  typeof activity.details === "object" &&
                  "status" in activity.details
                )
                  decisionStatus = activity.details.status;

                return (
                  <TimeLineItem
                    bullet={
                      <BulletItem
                        activityItem={activity}
                        decisionStatus={decisionStatus}
                      />
                    }
                    wrapperClassName="mt-8 h-full"
                    key={
                      activity.updated_at +
                      activity.activity_type +
                      activity.created_at
                    }
                    childrenClassName="w-full"
                  >
                    <ActivityNote
                      propertyName={propertyName}
                      applicationId={applicationId}
                      editNoteMeta={editNoteMeta}
                      deleteNoteMeta={deleteNoteMeta}
                      activityItem={activity}
                    />
                  </TimeLineItem>
                );
              })}
            </TimeLine>
          </div>
        )}
      </div>
    </div>
  );
};
